export interface SupportedPhoneCountry {
    name: string;
    iso2: string;
    dialCode: string;
    nationalNumberLength: number;
    flag: string;
}

export interface ParsedPhoneValue {
    matched: boolean;
    country?: SupportedPhoneCountry;
    dialCode: string;
    nationalNumber: string;
    fullNumber: string;
}

/**
 * 支持的国家列表（含名称、ISO2、区号、手机号长度、国旗）
 */
export const SUPPORTED_PHONE_COUNTRIES: SupportedPhoneCountry[] = [
    { name: "India", iso2: "IN", dialCode: "+91", nationalNumberLength: 10, flag: "🇮🇳" },
    { name: "Pakistan", iso2: "PK", dialCode: "+92", nationalNumberLength: 10, flag: "🇵🇰" },
    { name: "Sri Lanka", iso2: "LK", dialCode: "+94", nationalNumberLength: 9, flag: "🇱🇰" },
    { name: "Bangladesh", iso2: "BD", dialCode: "+880", nationalNumberLength: 10, flag: "🇧🇩" },
];

const DEFAULT_PHONE_COUNTRY = SUPPORTED_PHONE_COUNTRIES[0];

const DIAL_CODE_DESC = [ ...SUPPORTED_PHONE_COUNTRIES ].sort( ( a, b ) => b.dialCode.length - a.dialCode.length );

/**
 * 获取默认国家配置（当前为印度）
 */
export function getDefaultPhoneCountry(): SupportedPhoneCountry {
    return DEFAULT_PHONE_COUNTRY;
}

/**
 * 根据区号（如+91）查找对应国家对象
 */
export function getSupportedPhoneCountryByDialCode( dialCode?: string | null ): SupportedPhoneCountry | undefined {
    if ( !dialCode ) return undefined;
    return SUPPORTED_PHONE_COUNTRIES.find( ( country ) => country.dialCode === dialCode );
}

/**
 * 根据国家ISO2码（如IN）查找对应国家对象
 */
export function getSupportedPhoneCountryByIso2( iso2?: string | null ): SupportedPhoneCountry | undefined {
    if ( !iso2 ) return undefined;
    const normalizedIso2 = String( iso2 ).trim().toUpperCase();
    return SUPPORTED_PHONE_COUNTRIES.find( ( country ) => country.iso2 === normalizedIso2 );
}

/**
 * 只保留手机号中的数字字符
 */
export function sanitizePhoneInput( raw?: string | number | null ): string {
    return String( raw ?? "" ).replace( /\D/g, "" );
}

/**
 * 搜索传参使用的区号格式：去掉展示层的加号，只保留数字部分
 */
export function stripPhoneDialCodePrefix( dialCode?: string | number | null ): string {
    return String( dialCode ?? "" ).trim().replace( /^\+/, "" );
}

/**
 * 展示手机号时统一补齐区号前缀加号
 */
export function normalizePhoneDisplayDialCode( dialCode?: string | number | null ): string {
    const sanitizedDialCode = String( dialCode ?? "" ).trim();
    if ( !sanitizedDialCode ) return "";
    if ( sanitizedDialCode.startsWith( "+" ) ) return sanitizedDialCode;
    return `+${sanitizedDialCode}`;
}

/**
 * 从混合文本中提取出手机号（支持+、00开头等多种格式）
 */
export function extractPhoneFromMixedText( raw?: string | number | null ): string {
    const input = String( raw ?? "" );
    const plusIndex = input.indexOf( "+" );
    if ( plusIndex >= 0 ) {
        const plusAndDigits = input.slice( plusIndex ).replace( /[^+\d]/g, "" ).replace(
            /\+/g,
            ( match, offset ) => ( offset === 0 ? match : "" ),
        );
        if ( plusAndDigits.length > 1 ) return plusAndDigits;
    }

    // Only treat `00` as an international prefix when it starts the input
    // or appears at the beginning of a standalone non-digit-delimited segment.
    const zeroZeroMatch = input.match( /(?:^|[^\d])(00\d+)/ );
    if ( zeroZeroMatch?.[1] ) {
        return zeroZeroMatch[1];
    }

    return sanitizePhoneInput( input );
}

/**
 * 解析手机号，自动识别区号、国家、号段等，返回结构化结果
 * 支持+、00、纯数字等多种输入
 * @param raw 原始手机号
 * @param fallbackDialCode 兜底区号
 */
export function parsePhoneWithDialCode(
    raw?: string | number | null,
    fallbackDialCode?: string | null,
): ParsedPhoneValue {
    const extracted = extractPhoneFromMixedText( raw );
    const normalized = extracted.startsWith( "+" )
        ? extracted
        : extracted.startsWith( "00" )
        ? `+${extracted.slice( 2 )}`
        : extracted;

    if ( normalized.startsWith( "+" ) ) {
        const digitsAfterPlus = normalized.slice( 1 );
        for ( const country of DIAL_CODE_DESC ) {
            const dialDigits = country.dialCode.slice( 1 );
            if ( digitsAfterPlus.startsWith( dialDigits ) ) {
                const nationalNumber = sanitizePhoneInput( digitsAfterPlus.slice( dialDigits.length ) );
                return {
                    matched: true,
                    country,
                    dialCode: country.dialCode,
                    nationalNumber,
                    fullNumber: `${country.dialCode}${nationalNumber}`,
                };
            }
        }
    }

    const fallbackCountry = getSupportedPhoneCountryByDialCode( fallbackDialCode ) ?? DEFAULT_PHONE_COUNTRY;
    const nationalNumber = sanitizePhoneInput( normalized );

    return {
        matched: false,
        country: fallbackCountry,
        dialCode: fallbackCountry.dialCode,
        nationalNumber,
        fullNumber: nationalNumber ? `${fallbackCountry.dialCode}${nationalNumber}` : "",
    };
}

/**
 * 校验手机号长度是否符合国家规范
 * @param dialCode 区号
 * @param nationalNumber 本地号段
 */
export function validatePhoneLength( dialCode?: string | null, nationalNumber?: string | number | null ): boolean {
    const country = getSupportedPhoneCountryByDialCode( dialCode );
    if ( !country ) return false;
    return sanitizePhoneInput( nationalNumber ).length === country.nationalNumberLength;
}

/**
 * 格式化手机号展示（区号+空格+本地号段）
 */
export function formatPhoneDisplay( dialCode?: string | null, nationalNumber?: string | number | null ): string {
    const sanitizedNationalNumber = sanitizePhoneInput( nationalNumber );
    const sanitizedDialCode = normalizePhoneDisplayDialCode( dialCode );

    if ( sanitizedDialCode && sanitizedNationalNumber ) {
        return `${sanitizedDialCode} ${sanitizedNationalNumber}`;
    }
    if ( sanitizedDialCode ) return sanitizedDialCode;
    return sanitizedNationalNumber;
}

export interface PhoneFieldLike {
    phoneCountryCode?: string | null;
    phoneNationalNumber?: string | null;
    phoneCountryIso2?: string | null;
    phoneFullNumber?: string | null;
}

export interface ResolvePhoneValueOptions {
    phoneCountryCodeField?: string;
    phoneNationalNumberField?: string;
    phoneCountryIso2Field?: string;
    phoneFullNumberField?: string;
    legacyPhoneField?: string;
    fallbackDialCode?: string;
}

export interface ResolvedPhoneValue {
    country?: SupportedPhoneCountry;
    dialCode: string;
    nationalNumber: string;
    fullNumber: string;
    display: string;
    matched: boolean;
}

/**
 * 综合解析一组手机号相关字段，自动推断国家、区号、号段等
 * 支持多字段兼容和兜底逻辑，适用于后端返回的多样结构
 * @param source 原始对象
 * @param options 字段名映射、兜底区号等
 */
export function resolvePhoneValue(
    source?: Record<string, any> | null,
    options?: ResolvePhoneValueOptions,
): ResolvedPhoneValue {
    const phoneCountryCodeField = options?.phoneCountryCodeField ?? "phoneCountryCode";
    const phoneNationalNumberField = options?.phoneNationalNumberField ?? "phoneNationalNumber";
    const phoneCountryIso2Field = options?.phoneCountryIso2Field ?? "phoneCountryIso2";
    const phoneFullNumberField = options?.phoneFullNumberField ?? "phoneFullNumber";
    const legacyPhoneField = options?.legacyPhoneField ?? "phone";

    const rawDialCode = source?.[phoneCountryCodeField];
    const rawNationalNumber = source?.[phoneNationalNumberField];
    const rawIso2 = source?.[phoneCountryIso2Field];
    const rawFullNumber = source?.[phoneFullNumberField];
    const legacyPhone = source?.[legacyPhoneField];

    const countryByIso2 = getSupportedPhoneCountryByIso2( rawIso2 );
    const countryByDialCode = getSupportedPhoneCountryByDialCode( rawDialCode );
    const explicitCountry = countryByDialCode ?? countryByIso2;

    const explicitNationalNumber = sanitizePhoneInput( rawNationalNumber );
    const explicitDialCode = explicitCountry?.dialCode ?? String( rawDialCode ?? "" ).trim();

    if ( explicitDialCode || explicitNationalNumber || rawFullNumber ) {
        const parsed = parsePhoneWithDialCode(
            rawFullNumber || formatPhoneDisplay( explicitDialCode, explicitNationalNumber ),
            explicitDialCode || options?.fallbackDialCode,
        );
        const nationalNumber = explicitNationalNumber || parsed.nationalNumber;
        const dialCode = explicitDialCode || parsed.dialCode;
        const country = explicitCountry ?? parsed.country;
        const display = formatPhoneDisplay( dialCode, nationalNumber );
        return {
            country,
            dialCode,
            nationalNumber,
            fullNumber: rawFullNumber || `${dialCode}${nationalNumber}`,
            display,
            matched: parsed.matched || Boolean( explicitCountry ),
        };
    }

    const parsedLegacyPhone = parsePhoneWithDialCode( legacyPhone, options?.fallbackDialCode );
    return {
        country: parsedLegacyPhone.country,
        dialCode: parsedLegacyPhone.dialCode,
        nationalNumber: parsedLegacyPhone.nationalNumber,
        fullNumber: parsedLegacyPhone.fullNumber,
        display: formatPhoneDisplay( parsedLegacyPhone.dialCode, parsedLegacyPhone.nationalNumber ),
        matched: parsedLegacyPhone.matched,
    };
}
